import {
  NetworkError,
  deepCamelCase,
  deepSnakeCase,
  Select,
  searchQueryString,
  intoSnakeCase,
} from '../../fetchers/_helpers';
import {
  VideoQuestionFields,
  VideoQuestion,
} from '../../models/components/schemas/video-question/VideoQuestion';

/**
 * This file was automatically generated by @gctl/openapi-fetchers
 * Do not modify this file, any changes will be destroyed when
 * the command is rerun.
 */

export type GetVideoQuestionsFields = readonly VideoQuestionFields[];

/** Options for GET /videos/{video_id}/questions */
export type GetVideoQuestionsOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/json */
  select: Paths;
  where: {
    /**
     * The ID of the video.
     */
    videoId: number;
  };

  /** Possible query parameters for /videos/{video_id}/questions */
  query?: {
    /**
     * The privacy-enabled password required to access the video. The password can be sent as plaintext, such as `hunter2`, or as a SHA-1 hash, such as `f3bbbd66a63d4bf1747940578ec3d0103530e21d`.
     */
    password?: string;

    /**
     * The source of the request.
     *
     * Option descriptions:
     *  * `backend` - The request originates from backend systems.
     *  * `site` - The request originates from the Vimeo website.
     *  * `test` - The request originates from tests.
     *  * `upload` - The request originates from the upload flow.
     *
     */
    source?: `backend` | `site` | `test` | `upload`;
  };
};

/** Result type for GET /videos/{video_id}/questions */
export type GetVideoQuestionsResult<Paths extends readonly string[]> = Select<
  VideoQuestion,
  Paths
>[];

/**
 * Get a list of automatically generated questions about a video
 * This method returns a list of automatically generated questions about the specified video. The owner of the video must have AI viewer permissions.
 *
 * This method works only for videos with completed transcripts in English.
 */
export async function getVideoQuestions<Paths extends GetVideoQuestionsFields>({
  baseUrl,
  select,
  where: { videoId },
  query,
  ...options
}: GetVideoQuestionsOptions<Paths>): Promise<GetVideoQuestionsResult<Paths>> {
  let res = await fetch(
    `${baseUrl}/videos/${videoId}/questions?${searchQueryString(query)}&fields=${select
      .map(intoSnakeCase)
      .join(',')}`,
    {
      ...options,
      method: 'GET',
    },
  );

  if (!res.ok) {
    throw new NetworkError('A network error occurred', res.status, res);
  }

  if (res.status === 204) {
    return null as unknown as GetVideoQuestionsResult<Paths>;
  }

  if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
    throw new Error('Expected JSON response');
  }

  let json = await res.json();
  return deepCamelCase(json);
}

export type PostVideoQuestionsFields = readonly VideoQuestionFields[];

/** Options for POST /videos/{video_id}/questions */
export type PostVideoQuestionsOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/json */
  select: Paths;
  where: {
    /**
     * The ID of the video.
     */
    videoId: number;
  };

  /** Possible query parameters for /videos/{video_id}/questions */
  query?: {
    /**
     * The privacy-enabled password required to access the video. The password can be sent as plaintext, such as `hunter2`, or as a SHA-1 hash, such as `f3bbbd66a63d4bf1747940578ec3d0103530e21d`.
     */
    password?: string;

    /**
     * The electronic signature of the guest.
     */
    signature?: string;
  };

  /** Data serialized to body for POST /videos/{video_id}/questions */
  variables: {
    /**
     * The question that the user is asking about the video.
     * @example What is this video about?
     */

    question: string;
  };
};

/** Result type for POST /videos/{video_id}/questions */
export type PostVideoQuestionsResult<Paths extends readonly string[]> = Select<
  VideoQuestion,
  Paths
>;

/**
 * Get an automated answer to the user's question about a video
 * This method returns an automated answer to the user's question about the specified video.
 *
 * This method works only for videos with completed transcripts in English.
 */
export async function postVideoQuestions<Paths extends PostVideoQuestionsFields>({
  baseUrl,
  select,
  variables,
  where: { videoId },
  query,
  ...options
}: PostVideoQuestionsOptions<Paths>): Promise<PostVideoQuestionsResult<Paths>> {
  let res = await fetch(
    `${baseUrl}/videos/${videoId}/questions?${searchQueryString(query)}&fields=${select
      .map(intoSnakeCase)
      .join(',')}`,
    {
      ...options,
      method: 'POST',
      body: JSON.stringify(deepSnakeCase(variables)),
    },
  );

  if (!res.ok) {
    throw new NetworkError('A network error occurred', res.status, res);
  }

  if (res.status === 204) {
    return null as unknown as PostVideoQuestionsResult<Paths>;
  }

  if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
    throw new Error('Expected JSON response');
  }

  let json = await res.json();
  return deepCamelCase(json);
}
